import { Loader2, AlertTriangle, Info, CalendarClock, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * What is already true about a company's billing, shown while choosing a period.
 *
 * Billing is reconciled by hand, so the two ways to get a period wrong are
 * billing the same days twice and leaving a gap nobody notices. Neither is
 * visible from the order form on its own — cover end lives on the subscription,
 * unpaid invoices somewhere else, and a half-finished order for the same
 * company is invisible until it collides.
 *
 * Every date is shown rather than folded into a single suggestion, because the
 * person deciding needs to see what the suggestion was based on.
 */

const fmt = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const money = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const STATUS_TONE = {
    trial:        'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30',
    active:       'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/30',
    grace_period: 'bg-amber-500/10 text-amber-600 dark:text-amber-500 border-amber-500/30',
    expired:      'bg-destructive/10 text-destructive border-destructive/30',
    cancelled:    'bg-destructive/10 text-destructive border-destructive/30',
    suspended:    'bg-destructive/10 text-destructive border-destructive/30',
};

function Row({ label, value, strong }) {
    if (!value || value === '—') return null;
    return (
        <div className="flex justify-between gap-3 py-0.5">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className={strong ? 'font-medium text-foreground' : ''}>{value}</dd>
        </div>
    );
}

export default function BillingContextPanel({ context, loading, onUseSuggested, title }) {
    if (loading) {
        return (
            <div className="flex h-20 items-center justify-center rounded-lg border border-border bg-muted/20">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </div>
        );
    }
    if (!context) return null;

    const s = context.subscription || {};
    const warnings = context.warnings || [];
    const isTrial = s.status === 'trial';
    const isGrace = s.status === 'grace_period';

    return (
        <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {title || 'Where this company stands'}
                </p>
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[s.status] || 'border-border text-muted-foreground'}`}>
                    {String(s.status || 'unknown').replace('_', ' ')}
                    {s.planName ? ` · ${s.planName}` : ' · no plan'}
                </span>
            </div>

            <dl className="grid gap-x-6 text-xs sm:grid-cols-2">
                {isTrial && (
                    <>
                        <Row label="Trial started" value={fmt(s.trialStartDate)} />
                        <Row label="Trial ends" value={fmt(s.trialEndDate)} strong />
                    </>
                )}
                {isGrace && (
                    <Row label="Grace ends" value={fmt(s.gracePeriodEndDate)} strong />
                )}
                <Row label="Current period from" value={fmt(s.currentPeriodStart)} />
                <Row label="Current period to" value={fmt(s.currentPeriodEnd)} strong />
                <Row label="Billing cycle" value={s.billingCycle} />
                <Row label="Grace allowed" value={s.gracePeriodDays != null ? `${s.gracePeriodDays} days` : null} />
                <Row
                    label="Paid cover runs to"
                    value={s.coveredUntil ? fmt(s.coveredUntil) : 'nothing covered'}
                    strong
                />
                {context.totalOutstanding > 0 && (
                    <Row label="Outstanding" value={money(context.totalOutstanding)} strong />
                )}
            </dl>

            {context.lastActivatedOrder && (
                <p className="border-t border-border pt-2 text-[11px] text-muted-foreground">
                    Last activated: <strong className="text-foreground">{context.lastActivatedOrder.orderNumber}</strong>
                    {' · '}{fmt(context.lastActivatedOrder.periodStart)} → {fmt(context.lastActivatedOrder.periodEnd)}
                    {context.lastActivatedOrder.activatedWithoutPayment && ' · on credit terms'}
                </p>
            )}

            {warnings.length > 0 && (
                <ul className="space-y-1.5 border-t border-border pt-2">
                    {warnings.map((w, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                            {w.level === 'warn'
                                ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-500" />
                                : <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                            <span className={w.level === 'warn' ? 'text-foreground' : 'text-muted-foreground'}>
                                {w.text}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {context.suggestedPeriodStart && onUseSuggested && (
                <div className="flex flex-wrap items-start justify-between gap-2 border-t border-border pt-2">
                    <p className="flex items-start gap-2 text-xs text-muted-foreground">
                        <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                            Suggested start <strong className="text-foreground">{fmt(context.suggestedPeriodStart)}</strong>
                            {' — '}{context.suggestionReason}
                        </span>
                    </p>
                    <Button
                        size="sm" variant="outline" className="h-7 shrink-0 gap-1 text-xs"
                        onClick={() => onUseSuggested(context.suggestedPeriodStart)}
                    >
                        <Wand2 className="h-3 w-3" /> Use it
                    </Button>
                </div>
            )}

            {!context.billingReady && (
                <p className="flex items-start gap-2 border-t border-border pt-2 text-xs text-amber-600 dark:text-amber-500">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>Billing details are incomplete — the order can be drafted, but not invoiced yet.</span>
                </p>
            )}
        </div>
    );
}
